import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react'
import { Check, Minus } from 'lucide-react'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  className?: string
  indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked, defaultChecked, indeterminate, className = '', disabled, onChange, ...props }, forwardedRef) => {
    const isControlled = checked !== undefined
    const [localChecked, setLocalChecked] = useState(defaultChecked ?? false)
    const [isIndeterminate, setIsIndeterminate] = useState(indeterminate ?? false)
    const internalRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
      if (indeterminate !== undefined) {
        setIsIndeterminate(indeterminate)
        if (internalRef.current) {
          internalRef.current.indeterminate = indeterminate
        }
      }
    }, [indeterminate])

    // Sync local state if defaultChecked changes
    useEffect(() => {
      if (!isControlled && defaultChecked !== undefined) {
        setLocalChecked(defaultChecked)
      }
    }, [defaultChecked, isControlled])

    const handleChecked = isControlled ? checked : localChecked

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setLocalChecked(event.target.checked)
      }
      onChange?.(event)
    }

    // Intercept ref to monitor indeterminate property
    const refCallback = (node: HTMLInputElement | null) => {
      internalRef.current = node
      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }

      if (node) {
        // Monitor the indeterminate property
        setIsIndeterminate(node.indeterminate)
        
        // Overwrite the property setter so we are notified when it changes
        const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'indeterminate')
        if (descriptor && descriptor.set) {
          Object.defineProperty(node, 'indeterminate', {
            configurable: true,
            enumerable: true,
            get() {
              return descriptor.get?.call(this) ?? false
            },
            set(val) {
              descriptor.set?.call(this, val)
              setIsIndeterminate(val)
            }
          })
        }
      }
    }

    return (
      <label className={`relative inline-flex items-center justify-center h-5 w-5 select-none ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
        <input
          ref={refCallback}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={handleOnChange}
          className="sr-only peer"
          {...props}
        />
        <span
          className={`flex h-5 w-5 items-center justify-center rounded border transition-all duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-[#146971]/30
            ${(handleChecked || isIndeterminate)
              ? 'bg-[#146971] border-[#146971] text-white'
              : 'bg-white border-slate-300 hover:border-[#146971]'
            }
            ${disabled
              ? 'bg-slate-100 border-slate-200 text-slate-400 opacity-60'
              : ''
            }
          `}
        >
          {isIndeterminate ? (
            <Minus className="h-4 w-4 stroke-[3]" />
          ) : handleChecked ? (
            <Check className="h-4 w-4 stroke-[3]" />
          ) : null}
        </span>
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'
